import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {DndProvider} from "react-dnd";
import {HTML5Backend} from "react-dnd-html5-backend";

import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium, LabelXSmall} from "@khanacademy/wonder-blocks-typography";
import type {ImageInput} from "@khan/guide-types";

import {DragAndDropFileTarget} from "../../drag-drop-file-target/drag-drop-file-target.tsx";
import {ImageAttach} from "../image-attach.tsx";
import {ImagePreview} from "../image-preview.tsx";

import {PREVIEW_HEIGHT, PREVIEW_WIDTH} from "./constants.ts";
import {sharedStyles} from "./shared-styles.ts";
import type {SharedModalContentProps} from "./types.ts";

import imageIcon from "@phosphor-icons/core/regular/image.svg";
import plusCircleIcon from "@phosphor-icons/core/regular/plus-circle.svg";
import trashIcon from "@phosphor-icons/core/regular/trash-simple.svg";

interface Props extends SharedModalContentProps {
    // The file currently being dragged, or undefined if none.
    draggedFile: File | undefined;
    // Callback to set the dragged file.
    setDraggedFile: (file: File | undefined) => void;
    // Whether the drag target is active.
    overTarget: boolean;
    // Callback to set the drag target state.
    setOverTarget: (v: boolean) => void;
    // Callback to set the image from a file.
    handleSetImage: (dataURL: string, source: ImageInput["source"]) => void;
    // Callback to set the selected filename.
    setSelectedFilename: (filename: string | null) => void;
    // The image URL for preview.
    imageURL: string;
}

/**
 * Renders the file upload experience for the image attachment modal.
 * Displays a drag-and-drop area, file picker, and preview of the selected image.
 * Used as a content component within the image attach modal wrapper.
 */
export const FileUploadModalContent = ({
    image,
    draggedFile,
    setDraggedFile,
    overTarget,
    setOverTarget,
    handleSetImage,
    handleClearImage,
    selectedFilename,
    setSelectedFilename,
    imageURL,
}: Props) => (
    <>
        <View style={sharedStyles.content}>
            {/* @ts-expect-error - TS2322 - Type '{ children: Element; backend: BackendFactory; }' is not assignable to type 'IntrinsicAttributes & DndProviderProps<unknown, unknown>'. */}
            <DndProvider backend={HTML5Backend}>
                <DragAndDropFileTarget
                    onDrop={setDraggedFile}
                    onOver={setOverTarget}
                >
                    {image ? (
                        <View style={sharedStyles.previewContainer}>
                            <ImagePreview
                                displayHeight={PREVIEW_HEIGHT}
                                displayWidth={PREVIEW_WIDTH}
                                image={image.imageURL}
                            />
                        </View>
                    ) : (
                        <View
                            style={[
                                sharedStyles.previewContainer,
                                styles.dndTarget,
                                overTarget && styles.overTarget,
                            ]}
                        >
                            <PhosphorIcon icon={plusCircleIcon} size="medium" />
                            <View style={styles.dndText}>
                                <span>{t`Drag and Drop here or`}</span>
                                <ImageAttach
                                    image={image ?? null}
                                    onImageAttach={handleSetImage}
                                    fileDragged={draggedFile}
                                    setSelectedFilename={setSelectedFilename}
                                />
                            </View>
                        </View>
                    )}
                </DragAndDropFileTarget>
            </DndProvider>
        </View>
        <View style={styles.supportedFormats}>
            <LabelXSmall>{t`Supported formats: PNG, JPEG`}</LabelXSmall>
            <LabelXSmall>{t`Maximum size: 20 MB`}</LabelXSmall>
        </View>
        {image && (
            <View style={sharedStyles.imageFilenameContainer}>
                <View style={sharedStyles.imageFilenameRow}>
                    <PhosphorIcon icon={imageIcon} size="medium" />
                    <LabelMedium style={sharedStyles.imageFilenameOverflow}>
                        {selectedFilename}
                        {imageURL}
                    </LabelMedium>
                </View>
                <IconButton
                    icon={trashIcon}
                    kind="tertiary"
                    size="small"
                    onClick={handleClearImage}
                    aria-label={t`Clear image`}
                    testId="image-attach-clear-button"
                />
            </View>
        )}
    </>
);

const styles = StyleSheet.create({
    dndTarget: {
        backgroundColor: "#F3F4F5",
    },
    overTarget: {
        backgroundColor: "#E3F0FF",
        border: `2px dashed #1865F2`,
    },
    dndText: {
        flexDirection: "row",
        gap: spacing.xxxSmall_4,
        alignItems: "center",
    },
    supportedFormats: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        marginBlockStart: spacing.medium_16,
    },
});
